// src/components/KasrDaryaftForm.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toastApiError } from "../utils/toastErrors";
import { translateApiMessage } from "../utils/formErrors";
import { useDispatch, useSelector } from "react-redux";
import Select from "./ui/Select";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { useNavigate } from "react-router-dom";
import { UploadInputInterface } from "./ui/UploadInputInterface";
import {
  AlertTriangle,
  CalendarDays,
  FileUp,
  Hash,
  Loader2,
  User,
  UserRound,
  Wallet,
  X,
} from "lucide-react";

import axiosInstance from "../utils/axiosInstance";
import { fetchCostumers } from "../actions/authActions";
import { createKasrDaryaft } from "../actions/representationActions";

/* ---------- helpers ---------- */
const cx = (...classes) => classes.filter(Boolean).join(" ");

const extractErrorData = (axiosError) =>
  axiosError?.response?.data ?? {
    detail: axiosError?.message || "خطای نامشخص",
  };

const firstErr = (v) => (Array.isArray(v) ? v[0] : v);

/* ---------- Field ---------- */
const Field = ({
  id,
  name,
  label,
  placeholder,
  value,
  onChange,
  type = "text",
  required,
  icon: Icon,
  inputMode,
  min,
  step,
}) => {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="block text-sm font-medium text-slate-700 text-right"
      >
        {label}
        {required ? <span className="text-rose-500"> *</span> : null}
      </label>

      <div className="relative">
        {Icon ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
            <Icon size={18} />
          </span>
        ) : null}

        <input
          id={id}
          name={name}
          type={type}
          inputMode={inputMode}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          required={required}
          min={min}
          step={step}
          className={cx(
            "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-right text-slate-900",
            "placeholder:text-slate-400 shadow-sm",
            "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary",
            Icon ? "pr-10" : "pr-3"
          )}
        />
      </div>
    </div>
  );
};

/* ---------- Select styles (same look as Field) ---------- */
const useSelectStyles = (Icon) =>
  useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: "44px",
        borderRadius: "0.75rem",
        borderColor: state.isFocused ? "#93C5FD" : "#e2e8f0",
        boxShadow: state.isFocused
          ? "0 0 0 4px rgba(147,197,253,0.35)"
          : "none",
        backgroundColor: "#ffffff",
        paddingRight: Icon ? "34px" : "6px",
        paddingLeft: "6px",
        direction: "rtl",
        textAlign: "right",
        fontFamily: "Vazirmatn, sans-serif",
        ":hover": { borderColor: "#93C5FD" },
      }),
      placeholder: (base) => ({
        ...base,
        color: "#94a3b8",
        textAlign: "right",
      }),
      singleValue: (base) => ({
        ...base,
        color: "#0f172a",
        textAlign: "right",
      }),
      input: (base) => ({ ...base, textAlign: "right", color: "#0f172a" }),
      menu: (base) => ({
        ...base,
        direction: "rtl",
        borderRadius: "0.75rem",
        overflow: "hidden",
        zIndex: 80,
      }),
      option: (base, state) => ({
        ...base,
        textAlign: "right",
        backgroundColor: state.isSelected
          ? "#2563EB"
          : state.isFocused
          ? "#EFF6FF"
          : "#fff",
        color: state.isSelected ? "#fff" : "#0f172a",
      }),
      indicatorSeparator: (base) => ({ ...base, display: "none" }),
      dropdownIndicator: (base) => ({ ...base, paddingLeft: "10px" }),
      clearIndicator: (base) => ({ ...base, paddingLeft: "10px" }),
    }),
    [Icon]
  );

const SelectField = ({
  id,
  label,
  required,
  icon: Icon,
  isLoading,
  options,
  value,
  onChange,
  placeholder,
  error,
  inputValue,
  onInputChange,
  noOptionsMessage,
}) => {
  const styles = useSelectStyles(Icon);

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-700 text-right">
        {label}
        {required ? <span className="text-rose-500"> *</span> : null}
      </label>

      <div className="relative">
        {Icon ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 z-10 pointer-events-none">
            <Icon size={18} />
          </span>
        ) : null}

        <Select
          inputId={id}
          name={id}
          value={value}
          onChange={onChange}
          options={options}
          isLoading={!!isLoading}
          isDisabled={!!isLoading}
          isClearable
          placeholder={placeholder}
          noOptionsMessage={noOptionsMessage}
          styles={styles}
          className="text-right"
          inputValue={inputValue}
          onInputChange={onInputChange}
        />
      </div>

      {error ? (
        <div className="text-rose-700 text-sm mt-1 text-right">{error}</div>
      ) : null}
    </div>
  );
};

const KasrDaryaftForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    costumerList = [],
    customersLoading,
    customersError,
  } = useSelector((s) => s.costumers || {});

  const [formData, setFormData] = useState({
    declared_to: "",
    customer: "",
    date: "",
    cottage: "", // cottage_number (to_field='cottage_number')
    appeal_deadline: "", // NOW a date string "YYYY-MM-DD"
    total_value: "",
    is_appealed: false,
    is_paid: false,
    document: null,
  });

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [serverErrors, setServerErrors] = useState({});

  // --- cottage fetch (same pattern as ExpenseForm) ---
  const [cottages, setCottages] = useState([]);
  const [loadingCottages, setLoadingCottages] = useState(false);
  const [cottageSearch, setCottageSearch] = useState("");
  const reqSeq = useRef(0);

  const fetchCottages = async (searchTerm = "") => {
    setLoadingCottages(true);
    const mySeq = ++reqSeq.current;

    try {
      const params = new URLSearchParams();
      if (searchTerm?.trim()) params.set("search", searchTerm.trim());

      const url = `/cottages/${
        params.toString() ? `?${params.toString()}` : ""
      }`;
      const { data } = await axiosInstance.get(url);

      if (mySeq !== reqSeq.current) return;
      setCottages(data?.results || data || []);
    } catch (err) {
      if (mySeq !== reqSeq.current) return;
      setCottages([]);
      toastApiError(err, {}, "دریافت فهرست کوتاژها ناموفق بود.");
    } finally {
      if (mySeq === reqSeq.current) setLoadingCottages(false);
    }
  };

  useEffect(() => {
    dispatch(fetchCostumers());
  }, [dispatch]);

  useEffect(() => {
    fetchCottages("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchCottages(cottageSearch), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cottageSearch]);

  const customerOptions = useMemo(
    () =>
      (costumerList || []).map((c) => ({
        value: String(c.id),
        label: c.full_name,
      })),
    [costumerList]
  );

  const cottageOptions = useMemo(
    () =>
      (cottages || [])
        .map((c) => {
          const num = c.cottage_number ?? c.cottageNumber ?? c.number ?? "";
          return { value: String(num), label: String(num || c.id) };
        })
        .filter((o) => o.value && o.value !== "undefined"),
    [cottages]
  );

  const selectedCottageOption = useMemo(
    () => cottageOptions.find((o) => o.value === formData.cottage) || null,
    [cottageOptions, formData.cottage]
  );

  const handleSelectInputChange = (inputValue, actionMeta) => {
    if (actionMeta.action === "input-change") setCottageSearch(inputValue);
    return inputValue;
  };

  const toSingleCustomer = (id) =>
    customerOptions.find((o) => o.value === String(id)) || null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((p) => ({
      ...p,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDateChange = (val) => {
    setFormData((p) => ({ ...p, date: val ? val.format("YYYY-MM-DD") : "" }));
  };

  const handleAppealDeadlineChange = (val) => {
    setFormData((p) => ({
      ...p,
      appeal_deadline: val ? val.format("YYYY-MM-DD") : "",
    }));
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0] || null;
    setFormData((p) => ({ ...p, document: file }));
  };

  const fieldError = (key) =>
    serverErrors?.[key] ? firstErr(serverErrors[key]) : "";

  const canSubmit = useMemo(() => {
    return (
      formData.declared_to &&
      formData.customer &&
      formData.cottage &&
      formData.date &&
      formData.appeal_deadline &&
      formData.total_value !== ""
    );
  }, [formData]);

  const resetForm = () => {
    setErrorMsg("");
    setServerErrors({});
    setFormData({
      declared_to: "",
      customer: "",
      date: "",
      cottage: "",
      appeal_deadline: "",
      total_value: "",
      is_appealed: false,
      is_paid: false,
      document: null,
    });
    setCottageSearch("");
    fetchCottages("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setServerErrors({});
    setSubmitting(true);

    try {
      const data = new FormData();
      data.append("declared_to", formData.declared_to);
      data.append("customer", formData.customer);
      data.append("date", formData.date);
      data.append("cottage", formData.cottage);
      data.append("appeal_deadline", formData.appeal_deadline); // NOW date string
      data.append("total_value", String(formData.total_value ?? ""));
      data.append("is_appealed", formData.is_appealed ? "true" : "false");
      data.append("is_paid", formData.is_paid ? "true" : "false");
      if (formData.document instanceof File)
        data.append("document", formData.document);

      await dispatch(createKasrDaryaft(data));
      navigate("/kasrdaryaft-list");
    } catch (err) {
      const errData = extractErrorData(err);
      setServerErrors(errData);
      const msg =
        errData?.detail ||
        firstErr(errData?.non_field_errors) ||
        "خطا در ایجاد کسر دریافت";
      setErrorMsg(translateApiMessage(firstErr(msg)));
      toastApiError(err, {}, "خطا در ایجاد کسر دریافت");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-10 sm:mt-16 md:mt-20 lg:mt-24 xl:mt-28 px-3 sm:px-6 font-vazirmatn">
      <form onSubmit={handleSubmit} dir="rtl" className="max-w-3xl mx-auto">
        <div className="bg-white/95 backdrop-blur rounded-2xl shadow-lg border border-slate-200/80 overflow-hidden">
          {/* Header */}
          <div className="px-5 sm:px-7 py-5 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
            <div className="flex flex-row-reverse items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => navigate("/kasrdaryaft-list")}
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 transition"
              >
                <X size={18} />
                بازگشت
              </button>

              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <AlertTriangle size={22} />
                </div>
                <div className="text-right">
                  <h2 className="text-lg text-right sm:text-xl font-extrabold text-slate-800">
                    افزودن کسر دریافت
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                    اطلاعات کسر دریافت را وارد کنید و در صورت نیاز فایل را پیوست
                    کنید.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 sm:px-7 py-6">
            {customersError ? (
              <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-right text-rose-700 text-sm">
                خطا در دریافت لیست مشتریان
              </div>
            ) : null}

            {errorMsg ? (
              <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-right text-rose-700 text-sm">
                {errorMsg}
              </div>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField
                id="declared_to"
                label="اظهار شده به"
                required
                icon={UserRound}
                isLoading={customersLoading}
                options={customerOptions}
                value={toSingleCustomer(formData.declared_to)}
                onChange={(opt) =>
                  setFormData((p) => ({
                    ...p,
                    declared_to: opt ? opt.value : "",
                  }))
                }
                placeholder="انتخاب مشتری/شرکت مقصد"
                error={fieldError("declared_to")}
              />

              <SelectField
                id="customer"
                label="مشتری"
                required
                icon={User}
                isLoading={customersLoading}
                options={customerOptions}
                value={toSingleCustomer(formData.customer)}
                onChange={(opt) =>
                  setFormData((p) => ({ ...p, customer: opt ? opt.value : "" }))
                }
                placeholder="انتخاب مشتری"
                error={fieldError("customer")}
              />

              {/* Cottage (same as ExpenseForm behavior) */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700 text-right">
                  کوتاژ <span className="text-rose-500">*</span>
                </label>

                <div className="relative">
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 z-10 pointer-events-none">
                    <Hash size={18} />
                  </span>

                  <Select
                    name="cottage"
                    value={selectedCottageOption}
                    onChange={(selected) => {
                      setFormData((p) => ({
                        ...p,
                        cottage: selected ? selected.value : "",
                      }));
                    }}
                    onInputChange={handleSelectInputChange}
                    inputValue={cottageSearch}
                    options={cottageOptions}
                    isLoading={loadingCottages}
                    isClearable
                    placeholder={
                      loadingCottages
                        ? "در حال بارگذاری..."
                        : "جستجو و انتخاب کوتاژ"
                    }
                    noOptionsMessage={() =>
                      loadingCottages ? "در حال بارگذاری..." : "کوتاژ یافت نشد"
                    }
                    styles={useSelectStyles(Hash)}
                    className="text-right"
                  />
                </div>

                {fieldError("cottage") ? (
                  <div className="text-rose-700 text-sm mt-1 text-right">
                    {fieldError("cottage")}
                  </div>
                ) : null}
              </div>

              <Field
                id="total_value"
                name="total_value"
                label="مبلغ کل"
                placeholder="مثلاً 25000000"
                value={formData.total_value}
                onChange={handleChange}
                required
                type="number"
                icon={Wallet}
                inputMode="numeric"
                min={0}
                step={1}
              />

              {/* date */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700 text-right">
                  تاریخ <span className="text-rose-500">*</span>
                </label>

                <div className="relative">
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <CalendarDays size={18} />
                  </span>

                  <DatePicker
                    calendar={persian}
                    locale={persian_fa}
                    value={formData.date}
                    onChange={handleDateChange}
                    format="YYYY-MM-DD"
                    inputClass={cx(
                      "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-right text-slate-900",
                      "placeholder:text-slate-400 shadow-sm",
                      "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                    )}
                    containerClassName="w-full"
                    calendarPosition="bottom-right"
                  />
                </div>

                {fieldError("date") ? (
                  <div className="text-rose-700 text-sm mt-1 text-right">
                    {fieldError("date")}
                  </div>
                ) : null}
              </div>

              {/* appeal_deadline (NOW DATE) */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700 text-right">
                  مهلت اعتراض <span className="text-rose-500">*</span>
                </label>

                <div className="relative">
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <CalendarDays size={18} />
                  </span>

                  <DatePicker
                    calendar={persian}
                    locale={persian_fa}
                    value={formData.appeal_deadline}
                    onChange={handleAppealDeadlineChange}
                    format="YYYY-MM-DD"
                    inputClass={cx(
                      "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-right text-slate-900",
                      "placeholder:text-slate-400 shadow-sm",
                      "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                    )}
                    containerClassName="w-full"
                    calendarPosition="bottom-right"
                  />
                </div>

                {fieldError("appeal_deadline") ? (
                  <div className="text-rose-700 text-sm mt-1 text-right">
                    {fieldError("appeal_deadline")}
                  </div>
                ) : null}
              </div>
            </div>

            {/* Toggles + file */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-800">
                      وضعیت اعتراض
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      اگر اعتراض ثبت شده است، فعال کنید.
                    </div>
                  </div>

                  <label className="relative inline-flex items-center cursor-pointer select-none">
                    <input
                      type="checkbox"
                      name="is_appealed"
                      checked={!!formData.is_appealed}
                      onChange={handleChange}
                      className="sr-only peer"
                    />
                    <span
                      className="
                        relative w-11 h-6 rounded-full bg-slate-200 transition-colors
                        peer-checked:bg-primary
                        after:content-[''] after:absolute after:top-0.5 after:right-0.5 after:h-5 after:w-5
                        after:rounded-full after:bg-white after:shadow after:transition-transform
                        peer-checked:after:-translate-x-5
                      "
                    />
                    <span className="mr-3 text-sm font-medium text-slate-700">
                      اعتراض شده
                    </span>
                  </label>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-800">
                      وضعیت پرداخت
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      در صورت پرداخت/تسویه، فعال کنید.
                    </div>
                  </div>

                  <label className="relative inline-flex items-center cursor-pointer select-none">
                    <input
                      type="checkbox"
                      name="is_paid"
                      checked={!!formData.is_paid}
                      onChange={handleChange}
                      className="sr-only peer"
                    />
                    <span
                      className="
                        relative w-11 h-6 rounded-full bg-slate-200 transition-colors
                        peer-checked:bg-primary
                        after:content-[''] after:absolute after:top-0.5 after:right-0.5 after:h-5 after:w-5
                        after:rounded-full after:bg-white after:shadow after:transition-transform
                        peer-checked:after:-translate-x-5
                      "
                    />
                    <span className="mr-3 text-sm font-medium text-slate-700">
                      پرداخت شده
                    </span>
                  </label>
                </div>
              </div>

              <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-800">
                      فایل پیوست
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      تصویر یا PDF (اختیاری)
                    </div>
                  </div>
                  <div className="text-slate-400 mt-0.5">
                    <FileUp size={18} />
                  </div>
                </div>

                <div className="mt-3">
                  <UploadInputInterface
                    file={formData.document}
                    onFileChange={handleFileChange}
                  />
                </div>

                {fieldError("document") ? (
                  <div className="text-rose-700 text-sm mt-1 text-right">
                    {fieldError("document")}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 sm:px-7 py-4 border-t border-slate-200 bg-white">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={resetForm}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
                disabled={submitting}
              >
                پاک کردن فرم
              </button>

              <button
                type="submit"
                disabled={!canSubmit || submitting}
                className={cx(
                  "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white",
                  "bg-primary hover:opacity-95 active:opacity-90 transition shadow-sm",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    در حال ثبت...
                  </>
                ) : (
                  "ثبت"
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default KasrDaryaftForm;
